
















































































































































































import InputGroup from "@/components/InputGroup.vue";
import { UpstreamIdpId } from "@/graphql/types";
import injectStrict from "@/utilities/injectstrict";
import { maxLength, required, email } from "@/utilities/validators";
import {
  defineComponent,
  toRefs,
  ref,
  watch,
  computed,
} from "@vue/composition-api";
import useVuelidate from "@vuelidate/core";
import { UserKey } from "../symbols";
import useProfile from "@/composables/useProfile";
import {
  useLinkIdentityToUserMutation,
  useUnlinkLinkedIdentityMutation,
  useUpdateLinkedIdentityMutation,
} from "@/graphql/types";
import LinkedIdpEditControls from "./LinkedIdpEditControls.vue";
import AddIdentityForm from "./AddIdentityForm.vue";
import { ParentQueryKey } from "../symbols";

export default defineComponent({
  components: { InputGroup, LinkedIdpEditControls, AddIdentityForm },
  setup() {
    // Common variables
    const user = injectStrict(UserKey);
    const parentQuery = injectStrict(ParentQueryKey);
    const { isCentralAdmin, isLocalAdmin } = useProfile();
    const v$ = useVuelidate(useValidations(), { ...toRefs(user) });

    // NemLog-in variables
    const isEditingNemlogin = ref(false);
    const nemloginId = ref(user.nemloginId);
    const showAddNemloginIdentity = ref(false);
    const addNemLoginLinkedIdentityLoading = ref(false);
    const updateNemLoginLinkedIdentityLoading = ref(false);
    const unlinkNemLoginLinkedIdentityLoading = ref(false);

    // Local IdP variables
    const showAddLocalIdentity = ref(false);
    const localIdps$ = ref([] as UpstreamIdpId[]);
    const localIdpUserIds = ref<{ [id: string]: string }>({});
    const isEditingLocalIdpIds = ref<{ [id: string]: boolean }>({});
    const addLocalIdpLinkedIdentityLoading = ref(false);
    const updateLocalIdpLinkedIdentityLoading = ref<{ [id: string]: boolean }>(
      {}
    );
    const unlinkLocalIdpLinkedIdentityLoading = ref<{ [id: string]: boolean }>(
      {}
    );

    // GraphQL mutations
    const { mutate: updateLinkedIdentityMutate } =
      useUpdateLinkedIdentityMutation({});

    const { mutate: linkIdentityToUserMutate } = useLinkIdentityToUserMutation(
      {}
    );

    const { mutate: unlinkLinkedIdentityMutate } =
      useUnlinkLinkedIdentityMutation({});
    //End of GraphQL mutations

    // NemLog-in logic
    const editLinkedIdentity = () => {
      isEditingNemlogin.value = true;
    };

    const shouldUpdateNemloginId = computed(() => {
      return (
        nemloginId.value !== "" &&
        nemloginId.value !== null &&
        user.nemloginId !== nemloginId.value
      );
    });

    const toggleAddNemloginIdentity = () => {
      showAddNemloginIdentity.value = !showAddNemloginIdentity.value;
    };

    const onAddNemLoginLinkedIdentity = (newIdentity: any) => {
      addNemLoginLinkedIdentityLoading.value = true;

      onAddNewIdentity(true, newIdentity)?.finally(() => {
        addNemLoginLinkedIdentityLoading.value = false;
      });
    };

    const onUpdateNemLoginLinkedIdentity = (
      upStreamIdpUserId: string | undefined,
      newValue: string | undefined
    ) => {
      updateNemLoginLinkedIdentityLoading.value = true;

      onUpdateLinkedIdentity(upStreamIdpUserId, newValue, true)?.finally(() => {
        updateNemLoginLinkedIdentityLoading.value = false;
      });
    };

    const onUnlinkNemLoginLinkedIdentity = (
      upstreamIdpUserId: string | undefined
    ) => {
      unlinkNemLoginLinkedIdentityLoading.value = true;

      onUnlinkLinkedIdentity(upstreamIdpUserId, true)?.finally(() => {
        unlinkNemLoginLinkedIdentityLoading.value = false;
      });
    };
    //End of NemLog-in logic

    // Local IdP logic
    const editLocalIdp = (upstreamIdpId: string) => {
      isEditingLocalIdpIds.value = {
        ...isEditingLocalIdpIds.value,
        [upstreamIdpId]: true,
      };
    };

    const shouldUpdateLocalIdpId = (idp: UpstreamIdpId): boolean => {
      const newVal = localIdpUserIds.value[idp.upstreamIdpId];
      return !!newVal && newVal !== idp.upstreamIdpUserId;
    };

    const toggleAddLocalIdentity = () => {
      showAddLocalIdentity.value = !showAddLocalIdentity.value;
    };

    const hasLocalIdps = computed(() => {
      return localIdps$.value && localIdps$.value.length > 0;
    });

    const onAddLocalIdpLinkedIdentity = (newIdentity: any) => {
      addLocalIdpLinkedIdentityLoading.value = true;

      onAddNewIdentity(false, newIdentity)?.finally(() => {
        addLocalIdpLinkedIdentityLoading.value = false;
      });
    };

    const onUpdateLocalIdpLinkedIdentity = (
      upstreamIdpId: string | undefined,
      upstreamIdpUserId: string | undefined,
      newValue: string | undefined
    ) => {
      updateLocalIdpLinkedIdentityLoading.value = {
        ...updateLocalIdpLinkedIdentityLoading.value,
        [upstreamIdpId ?? ""]: true,
      };

      onUpdateLinkedIdentity(upstreamIdpUserId, newValue, false)?.finally(
        () => {
          updateLocalIdpLinkedIdentityLoading.value = {
            ...updateLocalIdpLinkedIdentityLoading.value,
            [upstreamIdpId ?? ""]: false,
          };
        }
      );
    };

    const onUnlinkLocalIdpLinkedIdentity = (
      upstreamIdpId: string | undefined,
      upstreamIdpUserId: string | undefined
    ) => {
      unlinkLocalIdpLinkedIdentityLoading.value = {
        ...unlinkLocalIdpLinkedIdentityLoading.value,
        [upstreamIdpId ?? ""]: true,
      };

      onUnlinkLinkedIdentity(upstreamIdpUserId, false)?.finally(() => {
        unlinkLocalIdpLinkedIdentityLoading.value = {
          ...unlinkLocalIdpLinkedIdentityLoading.value,
          [upstreamIdpId ?? ""]: false,
        };
      });
    };
    //End of Local IdP logic

    const isCanManageLinkedIdentities = computed(() => {
      return isCentralAdmin.value || isLocalAdmin.value;
    });

    const onAddNewIdentity = (isNemLogin: boolean, newIdentity: any) => {
      const isExistingIdp = user.upstreamIdpIds?.find(
        (idp) =>
          idp?.upstreamIdpUserId === newIdentity.upstreamIdpUserId &&
          idp?.upstreamIdpId === newIdentity.upstreamIdpId
      );

      if (isExistingIdp) {
        console.error("Identity already exists: ", isExistingIdp);
        return;
      }

      return linkIdentityToUserMutate(
        {
          input: {
            userId: user.id,
            isNemLogin: isNemLogin,
            upstreamIdpUserId: newIdentity.upstreamIdpUserId,
            upstreamIdpId: newIdentity.upstreamIdpId,
          },
        },
        {
          refetchQueries: [parentQuery],
          update: (cache) => {
            const newLocalIdp = {
              upstreamIdpUserId: newIdentity.upstreamIdpUserId,
              upstreamIdpId: newIdentity.upstreamIdpId,
            };
            user.upstreamIdpIds = [...(user.upstreamIdpIds ?? []), newLocalIdp];

            if (isNemLogin) {
              updateNemLoginUsersCache(
                cache,
                user.id,
                newIdentity.upstreamIdpUserId
              );
              user.nemloginId = newIdentity.upstreamIdpUserId;
              nemloginId.value = newIdentity.upstreamIdpUserId;
              showAddNemloginIdentity.value = false;
            } else {
              localIdps$.value = [...(localIdps$.value ?? []), newLocalIdp];

              localIdpUserIds.value = {
                ...localIdpUserIds.value,
                [newLocalIdp.upstreamIdpId]: newLocalIdp.upstreamIdpUserId,
              };

              isEditingLocalIdpIds.value = {
                ...isEditingLocalIdpIds.value,
                [newLocalIdp.upstreamIdpId]: false,
              };

              showAddLocalIdentity.value = false;
            }
          },
        }
      );
    };

    const onUnlinkLinkedIdentity = (
      upStreamIdpUserId: string | undefined,
      isNemLogin: boolean
    ) => {
      const upstreamIdp = user.upstreamIdpIds?.find(
        (idp) => idp?.upstreamIdpUserId === upStreamIdpUserId
      );

      if (!upstreamIdp) {
        console.error("No upstreamIdp found for user: ", user);
        return;
      }

      return unlinkLinkedIdentityMutate(
        {
          input: {
            userId: user.id,
            isNemLogin: isNemLogin,
            upstreamIdpId: upstreamIdp.upstreamIdpId,
            upstreamIdpUserId: upStreamIdpUserId ?? "",
          },
        },
        {
          update: (cache) => {
            if (isNemLogin) {
              user.nemloginId = "";
              nemloginId.value = "";
              updateNemLoginUsersCache(cache, user.id, "");
            }

            const targetIdp = user?.upstreamIdpIds?.find(
              (idp) => idp?.upstreamIdpUserId === upStreamIdpUserId
            );

            if (targetIdp) {
              user.upstreamIdpIds = user.upstreamIdpIds?.filter(
                (idp) => idp?.upstreamIdpUserId !== upStreamIdpUserId
              );

              localIdpUserIds.value = {
                ...localIdpUserIds.value,
                [targetIdp.upstreamIdpId]: "",
              };

              localIdps$.value = localIdps$.value.filter(
                (idp) => idp?.upstreamIdpId !== targetIdp.upstreamIdpId
              );

              delete isEditingLocalIdpIds.value[targetIdp.upstreamIdpId];
            }
          },
        }
      );
    };

    const onUpdateLinkedIdentity = (
      upStreamIdpUserId: string | undefined,
      newValue: string | undefined,
      isNemLogin: boolean
    ) => {
      const upstreamIdp = user.upstreamIdpIds?.find(
        (idp) => idp?.upstreamIdpUserId === upStreamIdpUserId
      );

      if (!upstreamIdp) {
        console.error("No upstreamIdp found for user: ", user);
        return;
      }

      return updateLinkedIdentityMutate(
        {
          input: {
            userId: user.id,
            isNemLogin: isNemLogin,
            upstreamIdpId: upstreamIdp.upstreamIdpId,
            upstreamIdpUserId: newValue ?? "",
            currentUpstreamIdpUserId: upstreamIdp.upstreamIdpUserId ?? "",
          },
        },
        {
          update: (cache) => {
            if (isNemLogin) {
              user.nemloginId = nemloginId.value;
              updateNemLoginUsersCache(cache, user.id, nemloginId.value ?? "");
            }

            const targetIdp = user?.upstreamIdpIds?.find(
              (idp) => idp?.upstreamIdpUserId === upStreamIdpUserId
            );

            if (targetIdp) {
              targetIdp.upstreamIdpUserId = newValue;
            }

            if (isNemLogin) {
              isEditingNemlogin.value = false;
            } else {
              isEditingLocalIdpIds.value = {
                ...isEditingLocalIdpIds.value,
                [upstreamIdp.upstreamIdpId]: false,
              };
            }
          },
        }
      );
    };

    const updateNemLoginUsersCache = (
      cache: any,
      userId: string,
      nemLoginValue: string
    ) => {
      const cachedData = cache.readQuery(parentQuery);
      if (!cachedData) return;

      const users = cachedData?.users?.nodes?.map((node: any) => {
        if (node?.id === userId) {
          return {
            ...node,
            nemloginId: nemLoginValue,
          };
        }
        return node;
      });

      cache.writeQuery({
        ...parentQuery,
        data: {
          ...cachedData,
          users: {
            ...cachedData?.users,
            nodes: users,
          },
        },
      });
    };

    watch(
      () => user,
      (newValue) => {
        const idps = (newValue.upstreamIdpIds ?? []).filter(
          (idp) => idp && idp.upstreamIdpUserId !== newValue.nemloginId
        ) as UpstreamIdpId[];

        localIdps$.value = idps;

        idps.forEach((idp) => {
          if (idp?.upstreamIdpId) {
            localIdpUserIds.value = {
              ...localIdpUserIds.value,
              [idp.upstreamIdpId]: idp.upstreamIdpUserId ?? "",
            };
            isEditingLocalIdpIds.value[idp.upstreamIdpId] = false;
            addLocalIdpLinkedIdentityLoading.value = false;
            updateLocalIdpLinkedIdentityLoading.value[idp.upstreamIdpId] =
              false;
            unlinkLocalIdpLinkedIdentityLoading.value[idp.upstreamIdpId] =
              false;
          }
        });

        nemloginId.value = newValue.nemloginId;
      },
      { immediate: true, deep: true }
    );

    return {
      v$,
      user,
      localIdps$,
      isCentralAdmin,
      onAddNewIdentity,
      editLinkedIdentity,
      onUnlinkLinkedIdentity,
      onUpdateLinkedIdentity,
      isCanManageLinkedIdentities,

      //NemLog-in
      nemloginId,
      isEditingNemlogin,
      shouldUpdateNemloginId,
      showAddNemloginIdentity,
      toggleAddNemloginIdentity,
      onAddNemLoginLinkedIdentity,
      onUnlinkNemLoginLinkedIdentity,
      onUpdateNemLoginLinkedIdentity,
      addNemLoginLinkedIdentityLoading,
      updateNemLoginLinkedIdentityLoading,
      unlinkNemLoginLinkedIdentityLoading,

      //Local IdP
      hasLocalIdps,
      editLocalIdp,
      localIdpUserIds,
      showAddLocalIdentity,
      isEditingLocalIdpIds,
      toggleAddLocalIdentity,
      shouldUpdateLocalIdpId,
      onAddLocalIdpLinkedIdentity,
      onUpdateLocalIdpLinkedIdentity,
      onUnlinkLocalIdpLinkedIdentity,
      addLocalIdpLinkedIdentityLoading,
      updateLocalIdpLinkedIdentityLoading,
      unlinkLocalIdpLinkedIdentityLoading,
    };
  },
});

function useValidations() {
  return {
    firstname: { required, maxLength: maxLength(255) },
    lastname: { required, maxLength: maxLength(255) },
    description: { maxLength: maxLength(1024) },
    email: { required, email },
    nemloginId: {},
  };
}
